// The state shown when Agent Chat is at capacity: the sitewide concurrency pool
// (Agent::ConcurrencyLimiter) refused this learner a seat, so the turn came back
// as a pre-stream 503 `pool_full`. This is NOT an error the learner caused — no
// agentic loop ran and nothing they've already earned is at risk — so we render
// a calm, reassuring full-panel state (mascot + BetaTag + title + body + a single
// primary action) rather than the red ConnectionErrorState. The one action
// re-attempts the turn: if a seat has since freed up the turn proceeds normally,
// otherwise the panel simply returns. Purely presentational — the retry lifecycle
// lives in the parent ChatContainer (handlePoolRetry), so this stays stateless.
//
// Rebuilt on the design-system Empty composition + Button (W-24314366), replacing
// the retired tds2_shim/agentforce/OverloadedState.
import React from 'react';
import PropTypes from 'prop-types';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent, Button } from '@trailhead-emu/design-system';
import BetaTag from 'components/agentforce/BetaTag';

export default function OverloadedState({
  mascotUrl,
  title = 'Agent Chat is busy right now',
  body = 'A lot of learners are building agents at the moment, so we’ve hit our ' +
    'capacity limit. Your progress is saved — take a short break and try again ' +
    'in a few minutes.',
  ctaLabel = 'Try again',
  retryingLabel = 'Trying again…',
  onRetry,
  retrying = false,
}) {
  return (
    <Empty
      variant="flush"
      className="tds2-agent-entry-state"
      role="status"
      aria-live="polite"
    >
      <EmptyHeader>
        {mascotUrl ? (
          <EmptyMedia variant="img">
            <img src={mascotUrl} alt="" aria-hidden="true" />
          </EmptyMedia>
        ) : null}
        <BetaTag />
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{body}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button
          variant="primary"
          size="small"
          spinner={retrying}
          disabled={retrying}
          onClick={onRetry}
        >
          {retrying ? retryingLabel : ctaLabel}
        </Button>
      </EmptyContent>
    </Empty>
  );
}

OverloadedState.propTypes = {
  mascotUrl: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  ctaLabel: PropTypes.string,
  retryingLabel: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  // True while a retry turn is in flight — swaps the label for a spinner and
  // disables the button so a learner can't stack admission attempts.
  retrying: PropTypes.bool,
};
